import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { useDebounce } from '@app/util/hooks/useDebounce'
import { Skeleton } from '@app/components/core/skeleton/Skeleton'

// Leaflet/MapLibre touch `window` at import time, so the map must stay out of
// the SSR module graph — load it lazily in a client-only chunk.
const SingleMarkerMap = lazy(() =>
  import('@app/components/core/map/SingleMarkerMap').then((module) => ({
    default: module.SingleMarkerMap,
  })),
)

type Coordinates = { lat: number; long: number }

export type LocationGeocodeAddressRefs = {
  country?: { id: number }
  region?: { id: number }
  municipality?: { id: number }
  locality?: { id: number }
}

export type LocationGeocodeResult = {
  lat: number
  long: number
  displayName?: string
  address?: LocationGeocodeAddressRefs
}

export type GeocodePayload = {
  street?: string
  postalCode?: string
  city?: string
}

type LocationMapPreviewProps = {
  address: string
  onGeocodeResult: (result: LocationGeocodeResult | null) => void
  buildGeocodePayload?: (address: string) => GeocodePayload
  debounceMs?: number
}

const defaultBuildGeocodePayload = (address: string): GeocodePayload => ({
  street: address,
})

/**
 * Geocodes a debounced address string and renders a map preview once
 * coordinates are resolved.
 */
export const LocationMapPreview = ({
  address,
  onGeocodeResult,
  buildGeocodePayload = defaultBuildGeocodePayload,
  debounceMs = 600,
}: LocationMapPreviewProps) => {
  const client = useClient()
  const t = useTranslations().app.common
  const trimmedAddress = address.trim()
  const debouncedAddress = useDebounce(trimmedAddress, debounceMs)
  const [coords, setCoords] = useState<Coordinates | null>(null)
  const [geocoding, setGeocoding] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!debouncedAddress) {
      setCoords(null)
      setGeocoding(false)
      setError(false)
      onGeocodeResult(null)
      return
    }

    let cancelled = false
    setGeocoding(true)
    setError(false)
    client
      .call('geocode-address', buildGeocodePayload(debouncedAddress))
      .then((result) => {
        if (cancelled) {
          return
        }

        const geo = result as LocationGeocodeResult | null
        if (
          geo &&
          typeof geo.lat === 'number' &&
          typeof geo.long === 'number'
        ) {
          setError(false)
          setCoords((prev) =>
            prev && prev.lat === geo.lat && prev.long === geo.long
              ? prev
              : { lat: geo.lat, long: geo.long },
          )
          onGeocodeResult(geo)
        } else {
          setError(true)
          setCoords(null)
          onGeocodeResult(null)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setCoords(null)
          onGeocodeResult(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGeocoding(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [buildGeocodePayload, client, debouncedAddress, onGeocodeResult])

  const isLoading =
    trimmedAddress !== '' && (trimmedAddress !== debouncedAddress || geocoding)

  const map = useMemo(
    () =>
      coords ? (
        <SingleMarkerMap
          lat={coords.lat}
          long={coords.long}
          marker="pin"
          height="16rem"
        />
      ) : null,
    [coords],
  )

  if (isLoading) {
    return <Skeleton height="16rem" borderRadius="20px" />
  }

  if (error) {
    return (
      <Skeleton
        height="16rem"
        borderRadius="20px"
        error
        errorMessage={t.locationNotFoundMessage}
      />
    )
  }

  if (!map) {
    return null
  }

  return <Suspense fallback={null}>{map}</Suspense>
}
